import React, { useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Group,
    Progress,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconUpload } from '@tabler/icons-react';

import {
    formatTrainingFileSize,
    getTrainingAcceptedTypesLabel,
    getTrainingFileAcceptAttr,
    normalizeTrainingAudiences,
    toTrainingCategorySelectOptions,
    validateTrainingFile,
} from '../../helpers/trainingHelpers';
import {
    TRAINING_AUDIENCE_LABELS,
    TrainingCategory,
} from '../../types/training';
import { useTrainingUpload } from '../../hooks/useTrainingUpload';
import When from '../common/When';

type Props = {
    categories: TrainingCategory[];
    onUploaded?: () => void;
};

export function TrainingUploadForm({ categories, onUploaded }: Props) {
    const { upload, uploading, uploadProgress } = useTrainingUpload();
    const [file, setFile] = useState<File | null>(null);
    const [resolvedFileType, setResolvedFileType] = useState<string | null>(
        null,
    );
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const form = useForm({
        initialValues: {
            title: '',
            description: '',
            categoryId: '',
        },
        validate: {
            title: value =>
                value.trim().length === 0 ? 'Title is required' : null,
            categoryId: value => (!value ? 'Category is required' : null),
        },
    });

    const categoryOptions = useMemo(
        () => toTrainingCategorySelectOptions(categories),
        [categories],
    );

    const selectedCategory = useMemo(
        () =>
            categories.find(
                category => String(category.id) === form.values.categoryId,
            ) ?? null,
        [categories, form.values.categoryId],
    );

    const disabled = categories.length === 0 || uploading;
    const disabledReason =
        categories.length === 0
            ? 'Create a category below before uploading'
            : undefined;

    const validateAndSetFile = (next: File) => {
        const validation = validateTrainingFile(next);
        if (!validation.ok) {
            notifications.show({
                title: 'Invalid file',
                message: validation.error,
                color: 'red',
            });
            setFile(null);
            setResolvedFileType(null);
            return;
        }

        setFile(next);
        setResolvedFileType(validation.mimeType);
        if (!form.values.title.trim()) {
            form.setFieldValue('title', next.name.replace(/\.[^/.]+$/, ''));
        }
    };

    const handleDragEvents = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) {
            return;
        }
        if (event.type === 'dragover') {
            setIsDragging(true);
        } else if (event.type === 'dragleave') {
            setIsDragging(false);
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (disabled) {
            return;
        }
        const next = event.dataTransfer.files[0];
        if (next) {
            validateAndSetFile(next);
        }
    };

    const handleDropzoneClick = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const handleDropzoneKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        event.preventDefault();
        handleDropzoneClick();
    };

    const handleFileInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const next = event.target.files?.[0];
        if (next) {
            validateAndSetFile(next);
        }
    };

    const handleSubmit = form.onSubmit(async values => {
        if (!file) {
            notifications.show({
                title: 'File required',
                message: 'Select a file to upload',
                color: 'red',
            });
            return;
        }

        await upload({
            file,
            title: values.title.trim(),
            description: values.description.trim() || undefined,
            categoryId: Number(values.categoryId),
            fileType: resolvedFileType ?? file.type,
        });

        form.setValues({
            title: '',
            description: '',
            categoryId: values.categoryId,
        });
        form.clearErrors();
        setFile(null);
        setResolvedFileType(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onUploaded?.();
    });

    const dropzoneClassName = [
        'relative border-2 border-dashed rounded-lg p-8 text-center transition-colors',
        disabled
            ? 'cursor-not-allowed opacity-60 border-gray-300 bg-gray-50'
            : 'cursor-pointer',
        !disabled && isDragging ? 'border-blue-500 bg-blue-50' : '',
        !disabled && !isDragging ? 'border-gray-300 hover:border-gray-400' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Select
                    label="Category"
                    placeholder={
                        categories.length === 0
                            ? 'Create a category first'
                            : 'Select a category'
                    }
                    data={categoryOptions}
                    searchable
                    disabled={categories.length === 0}
                    {...form.getInputProps('categoryId')}
                />
                <TextInput
                    label="Title"
                    placeholder="Material title"
                    {...form.getInputProps('title')}
                />
            </div>

            <Textarea
                label="Description"
                placeholder="Optional description"
                minRows={2}
                className="mb-4"
                {...form.getInputProps('description')}
            />

            <When
                condition={Boolean(selectedCategory)}
                fallback={
                    <p className="text-sm text-gray-500 mb-6">
                        Upload images, PDFs, or videos for the selected training
                        category. Visibility follows the category audience.
                    </p>
                }
            >
                <p className="text-sm text-gray-500 mb-6">
                    Audience:{' '}
                    {normalizeTrainingAudiences(selectedCategory?.audiences)
                        .map(
                            audience =>
                                TRAINING_AUDIENCE_LABELS[audience] ?? audience,
                        )
                        .join(', ')}
                    . Upload images, PDFs, or videos for this category.
                </p>
            </When>

            <Box
                className={dropzoneClassName}
                onDragOver={handleDragEvents}
                onDragLeave={handleDragEvents}
                onDrop={handleDrop}
                onClick={handleDropzoneClick}
                onKeyDown={handleDropzoneKeyDown}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={getTrainingFileAcceptAttr()}
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={disabled}
                />
                <When
                    condition={uploading}
                    fallback={
                        <When
                            condition={disabled}
                            fallback={
                                <div>
                                    <Text size="lg" fw={500} className="mb-2">
                                        <When
                                            condition={Boolean(file)}
                                            fallback="Click or drag and drop your file here"
                                        >
                                            {file?.name}
                                        </When>
                                    </Text>
                                    <Text size="sm" c="dimmed">
                                        <When
                                            condition={Boolean(file)}
                                            fallback={`Supported formats: ${getTrainingAcceptedTypesLabel()} · Images/PDF up to 50 MB · Video up to 500 MB`}
                                        >
                                            Click to choose a different file
                                        </When>
                                    </Text>
                                </div>
                            }
                        >
                            <div>
                                <Text size="lg" fw={500} className="mb-2">
                                    Upload disabled
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {disabledReason}
                                </Text>
                            </div>
                        </When>
                    }
                >
                    <div>
                        <Text size="lg" fw={500} className="mb-2">
                            Uploading {file?.name ?? 'file'}…
                        </Text>
                        <Text size="sm" c="dimmed">
                            Please keep this page open until the upload finishes
                        </Text>
                    </div>
                </When>
            </Box>

            <When condition={Boolean(file) && !uploading}>
                <Alert
                    color="teal"
                    icon={<IconCheck size={16} />}
                    className="mt-3"
                    title="File ready to upload"
                >
                    {file?.name} · {formatTrainingFileSize(file?.size ?? 0)}
                </Alert>
            </When>

            <Stack gap="sm" className="mt-4">
                <When condition={uploading}>
                    <Stack gap={6}>
                        <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                                Uploading material…
                            </Text>
                            <Text size="sm" c="dimmed">
                                {uploadProgress}%
                            </Text>
                        </Group>
                        <Progress
                            value={uploadProgress}
                            size="sm"
                            radius="xl"
                            animated
                            className="w-full"
                            color="primary"
                        />
                    </Stack>
                </When>
                <Group justify="flex-end">
                    <Button
                        type="submit"
                        size="sm"
                        className="w-fit"
                        leftSection={<IconUpload size={14} />}
                        loading={uploading}
                        disabled={!file || !form.values.categoryId || uploading}
                    >
                        Upload material
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}
